import { useRef, useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { addCreditCard, addCreditCardStateReset } from 'credit-cards/moneris/actions'
import { pendingSelector } from 'api/selectors'
import { creditCardSuccessfullyAddedSelector } from 'credit-cards/selectors'
import { guestUserCardVerifiedSelector } from 'booking/selectors'
import { verifyGuestCreditCardResetState } from 'booking/actions'
import featureToggles from 'shared/featureToggles'
import Button from '@components/Button'
import CircleLoader from '@components/CircleLoader'
import InfoModal from '@components/InfoModal'
import MonerisFrame from 'credit-cards/moneris/components/MonerisFrame'

import './styles.scss'

const createIframeMock = postMessage => ({
    contentWindow: {
        postMessage: () => {
            setTimeout(postMessage, 1000)
        },
    },
    src: 'fake-src',
})

const AddCreditCard = ({
    addCreditCard,
    pending: { monerisTokenization: pending },
    successCallback,
    shouldShowInfo = true,
    cardIsSuccessfullyAdded,
    addCreditCardStateReset,
    guestUserCardIsVerified,
}) => {
    const [isLoading, setIsLoading] = useState(false)
    const [isFrameLoading, setIsFrameLoading] = useState(true)
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
    const [error, setError] = useState(null)
    const iframeRef = useRef(null)
    const cardholderInputRef = useRef(null)
    const intl = useIntl()

    const generalErrorMessage = intl.formatMessage({ id: 'Moneris.Creditcard.Error.General' })
    const loading = isLoading || pending === true
    const errorMessage = error || (pending && pending instanceof Error && pending.message)
    const onLoad = () => {
        setIsFrameLoading(false)

        if (window.IS_MONERIS_IFRAME_MOCK) {
            iframeRef.current = createIframeMock(() => {
                featureToggles.SHOW_CREDIT_CARD_ADDED_POPUP && setIsInfoModalOpen(shouldShowInfo)
            })
        }
    }

    const onSubmit = () => {
        if (cardholderInputRef.current && !cardholderInputRef.current.value) {
            setError(generalErrorMessage)
            return
        }

        setError('')
        setIsLoading(true)
        iframeRef.current.contentWindow.postMessage('tokenize', `${MONERIS_TOKENIZATION_PAGE}/HPPtoken/index.php'`)
    }

    const addTokenHandler = (token, bin) => {
        addCreditCard(token, cardholderInputRef.current.value, bin)
    }

    const reloadForm = () => {
        setIsInfoModalOpen(false)
        if (successCallback) {
            successCallback({ forceClose: true })
        }
        setError('')
        setIsLoading(false)
        cardholderInputRef.current.value = ''
        iframeRef.current.src = iframeRef.current.src
        addCreditCardStateReset()
    }

    useEffect(() => {
        addCreditCardStateReset()
        verifyGuestCreditCardResetState()
    }, [])

    useEffect(() => {
        if (guestUserCardIsVerified && cardIsSuccessfullyAdded && successCallback) {
            successCallback({ forceClose: true })
            addCreditCardStateReset()
            verifyGuestCreditCardResetState()
        }
    }, [guestUserCardIsVerified])

    useEffect(() => {
        if (cardIsSuccessfullyAdded) {
            if (featureToggles.showCreditCardAddedPopup) {
                setIsInfoModalOpen(true)
            } else reloadForm()
        }

        if (cardIsSuccessfullyAdded === false) {
            setError(generalErrorMessage)
        }
    }, [cardIsSuccessfullyAdded])

    useEffect(() => {
        if (errorMessage) {
            setIsLoading(false)
        }
    }, [errorMessage])

    return (
        <>
            {isInfoModalOpen && (
                <InfoModal onDismiss={reloadForm} title="credit.card.confirm.title" text="credit.card.confirm.text" />
            )}
            <div className="moneris d-flex flex-column align-items-left">
                {isFrameLoading && <CircleLoader className="loader" />}
                <MonerisFrame
                    forwardedIframeRef={iframeRef}
                    forwardedInputRef={cardholderInputRef}
                    setToken={addTokenHandler}
                    setError={setError}
                    onLoad={onLoad}
                    isLoading={isFrameLoading}
                />
                {!isFrameLoading && (
                    <Button disabled={loading} isLoading={loading} onClick={onSubmit}>
                        <T>moneris.creditcard.submit.label</T>
                    </Button>
                )}
                {errorMessage && (
                    <p className="text-center mt-3 error text-capitalize">
                        <T>{errorMessage}</T>
                    </p>
                )}
            </div>
        </>
    )
}

export default compose(
    connect(
        createStructuredSelector({
            pending: pendingSelector,
            cardIsSuccessfullyAdded: creditCardSuccessfullyAddedSelector,
            guestUserCardIsVerified: guestUserCardVerifiedSelector,
        }),
        { addCreditCard, addCreditCardStateReset, verifyGuestCreditCardResetState }
    )
)(AddCreditCard)
